import React, { useState, useEffect } from 'react';
import {
   Box,
   Typography,
   TextField,
   Button,
   Paper,
   Container,
   Link,
   InputAdornment,
   IconButton,
   Grid,
   Divider,
   Snackbar,
   Alert,
   CircularProgress,
   Checkbox,
   FormControlLabel,
   Chip,
   Select,
   MenuItem,
   FormControl,
   InputLabel,
   Card,
   CardContent,
   CardActions,
   List,
   ListItem,
   ListItemIcon,
   ListItemText,
   SelectChangeEvent,
   useTheme,
   useMediaQuery,
   Tabs,
   Tab,
} from '@mui/material';
import {
   Email as EmailIcon,
   Lock as LockIcon,
   Person as PersonIcon,
   Visibility as VisibilityIcon,
   VisibilityOff as VisibilityOffIcon,
   Google as GoogleIcon,
   Facebook as FacebookIcon,
   Map as MapIcon,
   Explore as ExploreIcon,
   Flight as FlightIcon,
   Share as ShareIcon,
   AddPhotoAlternate as PhotoIcon,
   Check as CheckIcon,
   CreditCard as CreditCardIcon,
   EventNote as CalendarIcon,
   CloudUpload as CloudUploadIcon,
   People as PeopleIcon,
   Phone as PhoneIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
   getAuth,
   createUserWithEmailAndPassword,
   updateProfile,
   GoogleAuthProvider,
   signInWithPopup,
} from 'firebase/auth';
import { db } from '../firebase/config';
import {
   doc,
   setDoc,
   getDoc,
   updateDoc,
   deleteDoc,
   addDoc,
   collection,
   serverTimestamp,
   query,
   where,
   getDocs,
} from 'firebase/firestore';
import backgroundImage from '../assets/images/travel-background';
import AuthLayout from './AuthLayout';
import {
   trackUserAction,
   trackAnonymousPageView,
   trackAnonymousAction,
   ACTION_TYPES,
   ACTION_CATEGORIES,
   createAuthMetadata,
   createErrorMetadata,
   createPageMetadata,
} from '../utils/actionTracker';
import { useAuth } from '../contexts/AuthContext';
import { checkInvitedUser } from '../services/invitedUserService';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../firebase/config';
import AccountLinkingDialog from '../components/auth/AccountLinkingDialog';
import PhoneInput from '../components/common/PhoneInput';
import { Country, COUNTRIES } from '../data/countries';
// Removed Firebase phone auth service - now using Twilio backend API
// import phoneAuthService from '../services/phoneAuthService';

const SignupPage: React.FC = () => {
   const navigate = useNavigate();
   const { signUp } = useAuth();
   const [searchParams] = useSearchParams();
   const auth = getAuth();

   // Get return URL, email, and phone from query params
   const initialEmail = searchParams.get('email') || '';
   const phoneFromUrl = searchParams.get('phoneNumber');
   const invitationToken = searchParams.get('invitationToken');
   const referredBy = searchParams.get('referredBy');
   const isInvited = searchParams.get('invited') === 'true';

   // Form state
   // Phone signup disabled - always default to email
   const [signupType, setSignupType] = useState<'email' | 'phone'>('email');
   const [fullName, setFullName] = useState('');
   const [email, setEmail] = useState(initialEmail);
   const [password, setPassword] = useState('');
   const [confirmPassword, setConfirmPassword] = useState('');
   const [phoneNumber, setPhoneNumber] = useState(phoneFromUrl || '');
   const [selectedCountry, setSelectedCountry] = useState<Country>(COUNTRIES[0]); // Default to US
   const [verificationCode, setVerificationCode] = useState('');
   const [codeSent, setCodeSent] = useState(false);
   const [showPassword, setShowPassword] = useState(false);
   const [loading, setLoading] = useState(false);
   const [accountType, setAccountType] = useState<'personal' | 'organization'>('personal');
   const [travelFrequency, setTravelFrequency] = useState('');
   const [tripInterests, setTripInterests] = useState<string[]>([]);
   const [becomeCreator, setBecomeCreator] = useState(false);
   const [receiveInvites, setReceiveInvites] = useState(true);
   const [temporaryUserId, setTemporaryUserId] = useState<string | null>(null);
   const [agreeToTerms, setAgreeToTerms] = useState(false);

   // Error handling
   const [error, setError] = useState<string | null>(null);
   const [nameError, setNameError] = useState<string | null>(null);
   const [emailError, setEmailError] = useState<string | null>(null);
   const [passwordError, setPasswordError] = useState<string | null>(null);
   const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);
   const [phoneError, setPhoneError] = useState<string | null>(null);
   const [codeError, setCodeError] = useState<string | null>(null);
   const [showSuccessMessage, setShowSuccessMessage] = useState(false);
   const [termsError, setTermsError] = useState<string | null>(null);

   // Toast notifications
   const [showToast, setShowToast] = useState(false);
   const [toastMessage, setToastMessage] = useState('');
   const [toastSeverity, setToastSeverity] = useState<'success' | 'error' | 'warning' | 'info'>(
      'success'
   );

   // Account linking states
   const [showLinkingDialog, setShowLinkingDialog] = useState(false);
   const [potentialAccounts, setPotentialAccounts] = useState<any[]>([]);
   const [linkingPhoneNumber, setLinkingPhoneNumber] = useState<string>('');
   const [linkingEmail, setLinkingEmail] = useState<string>('');
   const [linkingSignupType, setLinkingSignupType] = useState<'phone' | 'email'>('phone');

   // Track page view on mount
   useEffect(() => {
      trackAnonymousPageView('/signup', 'Signup Page', {
         action: 'signup_page_visit',
         source: 'signup_page',
         context: 'authentication',
         accountType: accountType,
      });
   }, [accountType]);

   // Helper functions for toast notifications
   const showSuccessToast = (message: string, duration: number = 5000) => {
      setToastMessage(message);
      setToastSeverity('success');
      setShowToast(true);
      // Auto-hide success toast and navigate after duration
      setTimeout(() => {
         setShowToast(false);
      }, duration);
   };

   const showErrorToast = (message: string, duration: number = 10000) => {
      setToastMessage(message);
      setToastSeverity('error');
      setShowToast(true);
      // Auto-hide error toast after duration
      setTimeout(() => {
         setShowToast(false);
      }, duration);
   };

   const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') {
         return;
      }
      setShowToast(false);
   };

   const validateForm = () => {
      let isValid = true;
      setEmailError('');
      setNameError('');
      setPasswordError('');
      setConfirmPasswordError('');
      setPhoneError('');
      setCodeError('');
      setTermsError('');

      if (signupType === 'email') {
         // Email validation
         const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
         if (!emailRegex.test(email)) {
            setEmailError('Please enter a valid email address');
            isValid = false;
         }

         if (password.length < 8) {
            setPasswordError('Password must be at least 8 characters');
            isValid = false;
         }

         if (password !== confirmPassword) {
            setConfirmPasswordError('Passwords do not match');
            isValid = false;
         }
      } else {
         // Phone validation
         if (!phoneNumber.trim()) {
            setPhoneError('Please enter a phone number');
            isValid = false;
         }

         if (codeSent && !verificationCode.trim()) {
            setCodeError('Please enter the verification code');
            isValid = false;
         }
      }

      if (fullName.trim().length < 2) {
         setNameError('Please enter your full name');
         isValid = false;
      }

      if (!agreeToTerms) {
         setTermsError('You must agree to the Terms and Conditions to continue');
         isValid = false;
      }

      return isValid;
   };

   // Send phone verification code using Twilio backend API
   const sendPhoneVerification = async () => {
      if (!phoneNumber.trim()) {
         setPhoneError('Please enter a phone number');
         return;
      }

      setLoading(true);
      setError(null);
      setPhoneError('');

      // Combine country code with phone number
      const fullPhoneNumber = selectedCountry.dialCode + phoneNumber;

      try {
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/phone/send-verification`,
            {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({
                  phoneNumber: fullPhoneNumber,
               }),
            }
         );

         const data = await response.json();

         if (data.success) {
            setCodeSent(true);
            showSuccessToast('Verification code sent! Please check your phone.', 4000);
         } else {
            showErrorToast(data.error || 'Failed to send verification code');
         }
      } catch (error) {
         showErrorToast('Failed to send verification code. Please try again.');
      } finally {
         setLoading(false);
      }
   };

   // Handle account linking
   const handleLinkAccount = async (email: string, password: string) => {
      try {
         setLoading(true);
         setError(null);

         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/link-account`, {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify({
               phoneNumber: linkingSignupType === 'phone' ? linkingPhoneNumber : undefined,
               verificationCode: linkingSignupType === 'phone' ? verificationCode : undefined,
               email: email,
               password: password,
               signupType: linkingSignupType,
            }),
         });

         const data = await response.json();

         if (data.success) {
            // Sign in with custom token
            await signInWithCustomToken(auth, data.customToken);

            setShowLinkingDialog(false);
            showSuccessToast(data.message || 'Account linked successfully!', 3000);

            setTimeout(() => {
               navigate('/dashboard');
            }, 3000);
         } else {
            showErrorToast(data.error || 'Failed to link account');
         }
      } catch (error) {
         showErrorToast('Failed to link account. Please try again.');
      } finally {
         setLoading(false);
      }
   };

   // Handle creating new account instead of linking
   const handleCreateNewAccount = async () => {
      try {
         setShowLinkingDialog(false);
         // Continue with normal signup flow
         await proceedWithSignup();
      } catch (error) {
         setError('Failed to create account. Please try again.');
      }
   };

   // Separate function for the actual signup logic
   const proceedWithSignup = async () => {
      if (signupType === 'phone') {
         // Use Twilio backend API for phone signup
         try {
            const fullPhoneNumber = selectedCountry.dialCode + phoneNumber;

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/phone/signup`, {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({
                  phoneNumber: fullPhoneNumber,
                  verificationCode: verificationCode,
                  displayName: fullName,
                  invitationToken: invitationToken,
               }),
            });

            const data = await response.json();

            if (data.success && data.customToken) {
               // Sign in with the custom token from backend
               await signInWithCustomToken(auth, data.customToken);

               showSuccessToast('Account created successfully!', 3000);

               // Handle invitation if present
               if (invitationToken) {
                  // Try to process invitation
                  try {
                     const invitationResponse = await fetch(
                        `${process.env.REACT_APP_API_URL}/api/phone/process-invitation-signup`,
                        {
                           method: 'POST',
                           headers: {
                              'Content-Type': 'application/json',
                           },
                           body: JSON.stringify({
                              invitationToken: invitationToken,
                              userId: data.user.uid,
                           }),
                        }
                     );

                     if (invitationResponse.ok) {
                        const inviteData = await invitationResponse.json();
                        if (inviteData.success) {
                           setTimeout(() => {
                              navigate('/dashboard');
                           }, 3000);
                           return;
                        }
                     }
                  } catch (inviteError) {
                     console.error('Error processing invitation:', inviteError);
                  }
               }

               // Navigate to dashboard
               setTimeout(() => {
                  navigate('/dashboard');
               }, 3000);
            } else {
               // Handle specific error cases for phone signup
               let errorMessage = data.error || data.message || 'Failed to create account';

               showErrorToast(errorMessage);
            }
         } catch (error: any) {
            showErrorToast('Failed to create account. Please try again.');
         }
      } else {
         // Email signup - use existing server endpoint
         const requestBody: any = {
            displayName: fullName,
            invitationToken: invitationToken,
            email: email,
            password: password,
            referredBy: referredBy,
            invited: isInvited,
         };

         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/register`, {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
         });

         const data = await response.json();

         if (data.success) {
            showSuccessToast(data.message || 'Account created successfully!', 3000);

            setTimeout(() => {
               navigate('/welcome'); // Redirect to welcome page for regular users
            }, 3000);
         } else {
            // Handle specific error cases
            let errorMessage = data.error || data.message || 'Registration failed';

            showErrorToast(errorMessage);
         }
      }
   };

   const handleSignup = async () => {
      try {
         setLoading(true);
         setError(null);

         if (!validateForm()) {
            setLoading(false);
            return;
         }

         // Track signup attempt
         try {
            await trackAnonymousAction(
               ACTION_TYPES.SIGNUP,
               ACTION_CATEGORIES.USER,
               createAuthMetadata(
                  'signup_attempt',
                  signupType === 'email' ? email : phoneNumber,
                  signupType,
                  {
                     source: 'signup_form',
                     accountType: accountType,
                     travelFrequency: travelFrequency,
                     tripInterests: tripInterests,
                     becomeCreator: becomeCreator,
                     receiveInvites: receiveInvites,
                  }
               ),
               `Signup attempt with ${signupType}`
            );
         } catch (trackingError) {
            console.error('Failed to track signup attempt:', trackingError);
         }

         // Use the new signup flow that handles account linking
         await proceedWithSignup();

         // Track successful signup (if we get here, it was successful)
         try {
            await trackAnonymousAction(
               ACTION_TYPES.SIGNUP,
               ACTION_CATEGORIES.USER,
               createAuthMetadata(
                  'signup_success',
                  signupType === 'email' ? email : phoneNumber,
                  signupType,
                  {
                     source: 'signup_form',
                     fullName: fullName,
                     accountType: accountType,
                     travelFrequency: travelFrequency,
                     tripInterests: tripInterests,
                     becomeCreator: becomeCreator,
                     receiveInvites: receiveInvites,
                  }
               ),
               `Successful signup with ${signupType}`
            );
         } catch (trackingError) {
            console.error('Failed to track signup success:', trackingError);
         }
      } catch (error: any) {
         showErrorToast(error.message || 'Signup failed. Please try again.');

         // Track signup error
         try {
            await trackUserAction({
               actionType: ACTION_TYPES.SIGNUP,
               category: ACTION_CATEGORIES.USER,
               metadata: createErrorMetadata('signup_error', 'unknown', error.message, {
                  source: 'signup_form',
                  context: 'authentication',
                  email: email,
                  accountType: accountType,
               }),
            });
         } catch (trackingError) {
            console.error('Failed to track signup error:', trackingError);
         }
      } finally {
         setLoading(false);
      }
   };

   return (
      <AuthLayout>
         <Box sx={{ width: '100%', maxWidth: 420, mx: 'auto' }}>
            <Card
               elevation={6}
               sx={{
                  p: { xs: 2, sm: 4 },
                  borderRadius: 4,
                  boxShadow: 6,
                  mt: { xs: 2, md: 0 },
                  mb: 2,
                  bgcolor: 'background.paper',
                  position: 'relative',
                  overflow: 'visible',
               }}
            >
               <Typography
                  variant='h4'
                  component='h1'
                  fontWeight='bold'
                  color='primary'
                  align='center'
                  gutterBottom
               >
                  Create Your Account
               </Typography>
               <Typography
                  variant='subtitle1'
                  color='text.secondary'
                  align='center'
                  sx={{ mt: 1, mb: 3 }}
               >
                  Join Wetrippen to share and manage your trips
               </Typography>

               {/* Toast notifications replaced the inline alerts */}

               {/* Signup Type Tabs */}
               {/* Phone signup temporarily disabled - users should link phone numbers in profile settings */}
               {!phoneFromUrl && (
                  <Tabs
                     value={signupType}
                     onChange={(_, value) => setSignupType(value)}
                     sx={{ mb: 3 }}
                     centered
                  >
                     <Tab label='Email' value='email' />
                     {/* <Tab label='Phone' value='phone' /> */}
                  </Tabs>
               )}

               <Box
                  component='form'
                  onSubmit={(e) => {
                     e.preventDefault();
                     if (signupType === 'phone' && !codeSent) {
                        sendPhoneVerification();
                     } else {
                        handleSignup();
                     }
                  }}
               >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                     <TextField
                        fullWidth
                        label='Full Name'
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        error={!!nameError}
                        helperText={nameError}
                        InputProps={{
                           startAdornment: (
                              <InputAdornment position='start'>
                                 <PersonIcon color='primary' />
                              </InputAdornment>
                           ),
                        }}
                        required
                     />

                     {/* Phone signup form commented out - only email signup available */}
                     {signupType === 'email' ? (
                        <>
                           <TextField
                              fullWidth
                              label='Email Address'
                              type='email'
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              error={!!emailError}
                              helperText={emailError}
                              InputProps={{
                                 startAdornment: (
                                    <InputAdornment position='start'>
                                       <EmailIcon color='primary' />
                                    </InputAdornment>
                                 ),
                              }}
                              required
                           />

                           <TextField
                              fullWidth
                              label='Password'
                              type={showPassword ? 'text' : 'password'}
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              error={!!passwordError}
                              helperText={passwordError}
                              InputProps={{
                                 startAdornment: (
                                    <InputAdornment position='start'>
                                       <LockIcon color='primary' />
                                    </InputAdornment>
                                 ),
                                 endAdornment: (
                                    <InputAdornment position='end'>
                                       <IconButton
                                          onClick={() => setShowPassword(!showPassword)}
                                          edge='end'
                                       >
                                          {showPassword ? (
                                             <VisibilityOffIcon />
                                          ) : (
                                             <VisibilityIcon />
                                          )}
                                       </IconButton>
                                    </InputAdornment>
                                 ),
                              }}
                              required
                           />

                           <TextField
                              fullWidth
                              label='Confirm Password'
                              type={showPassword ? 'text' : 'password'}
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              error={!!confirmPasswordError}
                              helperText={confirmPasswordError}
                              InputProps={{
                                 startAdornment: (
                                    <InputAdornment position='start'>
                                       <LockIcon color='primary' />
                                    </InputAdornment>
                                 ),
                              }}
                              required
                           />
                        </>
                     ) : (
                        /* Phone signup temporarily disabled */
                        <>
                           {/* <PhoneInput
                              phoneNumber={phoneNumber}
                              selectedCountry={selectedCountry}
                              onPhoneNumberChange={setPhoneNumber}
                              onCountryChange={setSelectedCountry}
                              error={!!phoneError}
                              helperText={phoneError}
                              disabled={!!phoneFromUrl}
                              required
                              placeholder='Enter phone number'
                              label='Phone Number'
                           />

                           {codeSent && (
                              <TextField
                                 fullWidth
                                 label='Verification Code'
                                 value={verificationCode}
                                 onChange={(e) => setVerificationCode(e.target.value)}
                                 error={!!codeError}
                                 helperText={codeError || 'Enter the code sent to your phone'}
                                 InputProps={{
                                    startAdornment: (
                                       <InputAdornment position='start'>
                                          <LockIcon color='primary' />
                                       </InputAdornment>
                                    ),
                                 }}
                                 required
                              />
                           )} */}
                           <Alert severity='info' sx={{ mt: 2 }}>
                              Phone signup is temporarily disabled. Please sign up with email and
                              link your phone number in your profile settings.
                           </Alert>
                        </>
                     )}

                     <FormControlLabel
                        control={
                           <Checkbox
                              checked={agreeToTerms}
                              onChange={(e) => setAgreeToTerms(e.target.checked)}
                              color='primary'
                              disabled={loading}
                           />
                        }
                        label={
                           <Typography variant='body2'>
                              I agree to the{' '}
                              <Link
                                 component={RouterLink}
                                 to='/terms'
                                 target='_blank'
                                 rel='noopener noreferrer'
                                 underline='hover'
                                 color='primary'
                              >
                                 Terms and Conditions
                              </Link>
                           </Typography>
                        }
                        sx={{ alignItems: 'flex-center' }}
                     />
                     {termsError && (
                        <Typography
                           variant='caption'
                           color='error'
                           sx={{ display: 'block', mt: 0.5 }}
                        >
                           {termsError}
                        </Typography>
                     )}

                     <Button
                        fullWidth
                        variant='contained'
                        color='primary'
                        type='submit'
                        size='large'
                        disabled={loading || !agreeToTerms}
                        sx={{ mt: 2, fontWeight: 700, borderRadius: 2 }}
                     >
                        {loading ? (
                           <CircularProgress size={24} />
                        ) : (
                           /* signupType === 'phone' && !codeSent ? (
                           'Send Verification Code'
                        ) : */ 'Create Account'
                        )}
                     </Button>
                  </Box>
               </Box>

               <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography variant='body2' color='text.secondary'>
                     Already have an account?{' '}
                     <Link component={RouterLink} to='/login' fontWeight='medium'>
                        Sign in
                     </Link>
                  </Typography>
               </Box>
            </Card>
         </Box>

         {/* Account Linking Dialog */}
         <AccountLinkingDialog
            open={showLinkingDialog}
            onClose={() => setShowLinkingDialog(false)}
            potentialAccounts={potentialAccounts}
            phoneNumber={linkingPhoneNumber}
            email={linkingEmail}
            signupType={linkingSignupType}
            onLinkAccount={handleLinkAccount}
            onCreateNewAccount={handleCreateNewAccount}
            loading={loading}
            error={error}
         />

         {/* Toast Notification */}
         <Snackbar
            open={showToast}
            autoHideDuration={toastSeverity === 'success' ? 5000 : 6000}
            onClose={handleToastClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
         >
            <Alert
               onClose={handleToastClose}
               severity={toastSeverity}
               variant='filled'
               sx={{ width: '100%' }}
            >
               {toastMessage}
            </Alert>
         </Snackbar>
      </AuthLayout>
   );
};

export default SignupPage;
